import { Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { FiChevronsRight } from 'react-icons/fi';
import { Form } from '@ui';
import { FormActions } from '@features/forms/FormActions/FormActions';
import { fromZonedTime } from 'date-fns-tz';
import { toast } from 'react-toastify';
import { OCSForm } from '@lp/features/forms/OcsStatusForms/types';
import { getOcsDefaultValues, ocsSchema } from '@lp/features/forms/OcsStatusForms/OffOcsRegularForm/config';
import { getColorsByOrganType, isRandomizationScs, ocsToScs, ORGAN_ICON } from '@/utils/helpers';
import { Case } from '@/api/apis/case';
import { OCSTimeFields } from '../../../forms/OcsStatusForms/components/OCSTimeFields';
import { useOnOcs } from '@/queries/case';
import { retrieveUnifiedError } from '@/api/utils';
import { DeviceImage } from '../DeviceImage';

type OnOcsModalProps = {
  caseData: Case;
  onOpenChange: () => void;
};

export const OnOcsModal = ({ caseData, onOpenChange }: OnOcsModalProps) => {
  const { caseId, randomization, organType, donorHospitalDetails } = caseData;
  const OrganIcon = ORGAN_ICON[organType];
  const colors = getColorsByOrganType(organType);
  const { mutateAsync } = useOnOcs();
  const isScs = isRandomizationScs(randomization);

  const handleSubmit = async (values: OCSForm) => {
    const { date, time, timeZone } = values;
    const timestamp = fromZonedTime(new Date(`${date} ${time}`), timeZone).toISOString();
    try {
      await mutateAsync({
        caseId,
        data: { timestamp, timeZone },
      });
      onOpenChange();
      toast.success('Status successfully updated');
    } catch (error) {
      const { message } = retrieveUnifiedError(error);
      toast.error(message);
    }
  };

  return (
    <Modal
      isOpen
      onOpenChange={onOpenChange}
      size="xl"
      classNames={{
        wrapper: 'flex-wrap z-[420]',
        header: 'pb-0',
        footer: 'flex flex-col gap-6 pt-0 pb-6',
        closeButton: 'text-hl',
        backdrop: 'z-[419]',
      }}
    >
      <Form defaultValues={getOcsDefaultValues(donorHospitalDetails)} schema={ocsSchema} onSubmit={handleSubmit}>
        <ModalContent>
          <ModalHeader className="flex flex-col gap-2">
            <h3 className="text-hl text-base-10">
              {organType} {ocsToScs(randomization, 'On OCS')}
            </h3>
            <Divider />
          </ModalHeader>
          <ModalBody className="py-6">
            <div className="flex flex-col gap-6">
              <div className="w-full justify-center flex items-center mb-4">
                <OrganIcon className={`${colors.text50} w-[64px] h-auto`} />
                <FiChevronsRight className={colors.text50} size={40} />
                <DeviceImage isScs={isScs} ocsColor={colors.text50} />
              </div>
              <OCSTimeFields
                heading={ocsToScs(randomization, 'Confirmed Connection to OCS Time')}
                subHeading=""
                dateFieldName="date"
                timeFieldName="time"
                timezoneFieldName="timeZone"
                footerText={isScs ? '' : 'Perfusion Time will be calculated automatically'}
                compactView
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Divider />
            <div className="flex gap-2 justify-end">
              <FormActions
                actions={{
                  size: 'md',
                  discard: {
                    label: 'Discard',
                    handler: onOpenChange,
                  },
                  submit: {
                    label: 'Update status',
                  },
                }}
              />
            </div>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
